import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

export default function PastListings() {
  const [listings, setListings] = useState([]);
  const scrollerRef = useRef(null);

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: "smooth" });
  };

  return (
    <section id="listings" data-testid="listings-section" className="relative bg-[#0A0A0A] py-32 md:py-44 overflow-hidden">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="flex items-end justify-between mb-16 md:mb-20 gap-6">
          <div>
            <p className="overline text-[#CBBFAE] mb-6">Listings</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight text-white max-w-2xl">
              Curated <em className="text-[#CBBFAE]">Archives.</em>
            </h2>
            <p className="mt-6 max-w-lg text-white/60 leading-relaxed">
              Take a look at some of our favorite listings over the years.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              data-testid="listings-prev"
              onClick={() => scrollBy(-1)}
              aria-label="Previous"
              className="w-12 h-12 rounded-full border border-white/20 hover:border-[#CBBFAE] text-white/80 hover:text-[#CBBFAE] transition-colors flex items-center justify-center"
            >←</button>
            <button
              data-testid="listings-next"
              onClick={() => scrollBy(1)}
              aria-label="Next"
              className="w-12 h-12 rounded-full border border-white/20 hover:border-[#CBBFAE] text-white/80 hover:text-[#CBBFAE] transition-colors flex items-center justify-center"
            >→</button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        data-testid="listings-horizontal-scroll"
        className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory gap-8 md:gap-14 px-6 md:px-12 pb-6"
        style={{ scrollPaddingLeft: "3rem" }}
      >
        {listings.map((item, i) => (
          <motion.article
            key={item.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: Math.min(i * 0.06, 0.4) }}
            className="group snap-start shrink-0 w-[80vw] sm:w-[55vw] md:w-[36vw] lg:w-[30vw]"
          >
            <div className="relative overflow-hidden aspect-[4/5] bg-[#121212]">
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                style={{ filter: "grayscale(30%) brightness(0.92)" }}
              />
              <div className="absolute top-4 left-4 overline text-white/90 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
                {item.status}
              </div>
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white">
                <div>
                  <div className="font-serif text-2xl md:text-3xl leading-none">{item.title}</div>
                  <div className="text-white/70 text-sm mt-2">{item.location}</div>
                </div>
                {item.year && <div className="overline text-[#CBBFAE]">{item.year}</div>}
              </div>
            </div>
            <div className="mt-5 flex justify-end text-white/60 text-sm min-h-[1rem]" />
          </motion.article>
        ))}
        <div className="shrink-0 w-4 md:w-12" />
      </div>
    </section>
  );
}
